import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import store from './store/store';
import { I18nextProvider } from 'react-i18next';
import { LanguageProvider } from './i18n/LanguageContext';
import i18n from './i18n';
import { DarkModeProvider } from './DarkModeContext';
import { AuthProvider } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { SidePanelProvider } from './context/SidePanelContext';
import { WhiteLabelProvider } from './context/WhiteLabelContext';
import { pdfjs } from 'react-pdf'; // Importing pdfjs for worker configuration
import { PDF_WORKER_URL } from './config';
import AppRouter from './AppRouter';
import ChunkErrorBoundary from './components/ErrorBoundary/ChunkErrorBoundary';
import { setupChunkLoadErrorHandlers } from './utils/chunkLoadErrorHandler';
// import { startVersionCheck } from './utils/versionChecker'; // DISABLED: Version check popup not working in production
import './index.css';

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV || 'development',
    integrations: [new BrowserTracing()],
    tracesSampleRate: 0.1,
  });
}

// Suppress Google Maps API duplicate loading errors (non-critical)
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason?.message || reason?.toString?.() || '';
  if (message.includes('Google Maps') ||
    message.includes('c.UI') ||
    message.includes('H.iN') ||
    message.includes('google.maps')) {
    event.preventDefault();
    console.warn('[Suppressed] Google Maps API error:', message);
  }
});

// Also suppress window errors from Google Maps
window.addEventListener('error', (event) => {
  const message = event.message || '';
  const filename = event.filename || '';
  if (filename.includes('maps.googleapis.com') ||
    message.includes('c.UI') ||
    message.includes('H.iN')) {
    event.preventDefault();
    console.warn('[Suppressed] Google Maps error:', message);
    return true;
  }
});

pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL; // configurable worker URL

// Unregister any existing service workers to prevent CORS issues
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
      console.log('[SW] Unregistered service worker:', registration.scope);
    }
  }).catch((err) => {
    console.warn('[SW] Error unregistering service workers:', err);
  });
}

// Setup global chunk load error handlers
setupChunkLoadErrorHandlers();

// DISABLED: Version checking popup - not working properly in production
// if (process.env.NODE_ENV === 'production') {
//   startVersionCheck();
// }


const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <ChunkErrorBoundary>
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <LanguageProvider>
            <DarkModeProvider>
              <AuthProvider>
                <WhiteLabelProvider>
                  <ChatProvider>
                    <SidePanelProvider>
                      <AppRouter />
                    </SidePanelProvider>
                  </ChatProvider>
                </WhiteLabelProvider>
              </AuthProvider>
            </DarkModeProvider>
          </LanguageProvider>
        </I18nextProvider>
      </Provider>
    </ChunkErrorBoundary>
  </React.StrictMode>
);
// Frontend rebuild trigger 1770370481
